// Client Portal: report a fault on one of the customer's own lifts (Session AUD, email system Phase 5,
// Item 6). Reached from the Overview, the Work in progress page and each lift card
// (/portal/report-fault?lift=<id> chooses that lift).
//
// SAFETY FIRST (Director, binding). This form NEVER handles an entrapment or an emergency. Before the
// form, the page says plainly that anyone trapped in a lift, or any emergency, must phone
// 0203 576 4760 now and must not use this form. The form has no "someone is trapped" or "emergency"
// option at all, and the server refuses one if a request carries it.
//
// THREE STEPS: the form, a check of what will be sent (the branded confirm, never window.confirm), and
// a thank-you that says the office will be in touch, without promising a time (no office hours are
// defined anywhere on the platform). The office sees the report at once; nothing here emails anyone.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PortalOverview, PortalFaultReported } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";

export const FAULT_DESCRIPTION_MAX = 1500;
type ServiceAnswer = "yes" | "no" | "unsure";

interface LiftChoice {
  assetId: string;
  label: string;
}

// Every lift the customer holds, across their sites, labelled "Site · ELE-NNN-LL". Pure.
export function liftChoices(overview: PortalOverview): LiftChoice[] {
  const out: LiftChoice[] = [];
  for (const site of overview.sites) {
    for (const lift of site.lifts) out.push({ assetId: lift.assetId, label: `${site.name} · ${lift.ref}` });
  }
  return out;
}

export function SafetyNotice() {
  return (
    // Inline colours: Tailwind emits nothing for an alpha on a var() colour. #FF5A5A is the locked app-UI danger token.
    <div role="alert" className="mb-6 rounded-ele border p-4" style={{ borderColor: "rgba(255, 90, 90, 0.6)", background: "rgba(255, 90, 90, 0.08)" }}>
      <p className="font-heading text-lg text-ele-white">Is someone trapped in a lift, or is this an emergency?</p>
      <p className="mt-1 text-sm text-ele-white">
        Phone us now on{" "}
        <ContactValue value="0203 576 4760" kind="phone" className="font-bold underline" /> and do not use this form. This form is
        for faults only, and it is not watched around the clock.
      </p>
    </div>
  );
}

export function PortalReportFault() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [overview, setOverview] = useState<PortalOverview | null>(null);
  const [error, setError] = useState(false);
  const [assetId, setAssetId] = useState("");
  const [description, setDescription] = useState("");
  const [service, setService] = useState<ServiceAnswer>("unsure");
  const [step, setStep] = useState<"form" | "check" | "done">("form");
  const [formError, setFormError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<PortalFaultReported | null>(null);

  useEffect(() => {
    let live = true;
    api.portal
      .overview()
      .then((o) => {
        if (!live) return;
        setOverview(o);
        const wanted = params.get("lift");
        const choices = liftChoices(o);
        if (wanted && choices.some((c) => c.assetId === wanted)) setAssetId(wanted);
        else if (choices.length === 1) setAssetId(choices[0]!.assetId);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate, params]);

  const choices = useMemo(() => (overview ? liftChoices(overview) : []), [overview]);
  const chosen = choices.find((c) => c.assetId === assetId) ?? null;

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  function check() {
    if (!chosen) return setFormError("Choose the lift that has the fault.");
    if (description.trim().length < 5) return setFormError("Tell us what is wrong with the lift.");
    setFormError(null);
    setStep("check");
  }

  async function send() {
    if (!chosen || sending) return;
    setSending(true);
    setFormError(null);
    try {
      const res = await api.portal.reportFault({
        assetId: chosen.assetId,
        description: description.trim(),
        ...(service === "unsure" ? {} : { outOfService: service === "yes" }),
      });
      setSent(res);
      setStep("done");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return navigate("/portal/login", { replace: true });
      const status = err instanceof ApiError ? err.status : undefined;
      setFormError(
        status === 429
          ? "You have sent several reports in the last hour. Please call the office on 0203 576 4760."
          : status === 404
            ? "We could not find that lift on your account. Please call the office on 0203 576 4760."
            : "Your report could not be sent. Please try again, or call the office on 0203 576 4760.",
      );
      setStep("form");
    } finally {
      setSending(false);
    }
  }

  if (error) {
    return (
      <PortalShell>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!overview) return <FullScreenLoader />;

  const box = "rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4";
  const serviceLabel = service === "yes" ? "Yes, it is out of service" : service === "no" ? "No, it is still working" : "Not sure";

  return (
    <PortalShell wide userName={overview.customerName} onSignOut={logout}>
      <header className="mb-4">
        <h1 className="font-heading text-3xl tracking-wide text-ele-white">Report a fault</h1>
        <p className="mt-1 text-ele-body">Tell us about a problem with one of your lifts and our office will arrange the next step.</p>
      </header>

      <SafetyNotice />

      {step === "done" ? (
        <div className={box} role="status">
          <p className="font-heading text-xl text-ele-green">Thank you, your report has been sent</p>
          <p className="mt-2 text-sm text-ele-white">
            Our office has your report{sent?.liftRef ? ` for ${sent.liftRef}` : ""} and will be in touch.
            {sent?.reference ? ` Your reference is ${sent.reference}.` : ""}
          </p>
          <p className="mt-2 text-sm text-ele-body">You can follow it under Work in progress once an engineer is arranged.</p>
          <div className="mt-4 flex flex-wrap gap-4">
            <Link to="/portal/work" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
              See work in progress
            </Link>
            <Link to="/portal" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
              Back to your overview
            </Link>
          </div>
        </div>
      ) : choices.length === 0 ? (
        <p className={`${box} text-sm text-ele-body`}>
          There are no lifts on your account to report a fault on. Please call the office on{" "}
          <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
        </p>
      ) : step === "check" && chosen ? (
        <div className={box}>
          <h2 className="font-heading text-xl text-ele-purple">Check your report</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <div>
              <dt className="text-ele-body">Lift</dt>
              <dd className="text-ele-white">{chosen.label}</dd>
            </div>
            <div>
              <dt className="text-ele-body">What is wrong</dt>
              <dd className="whitespace-pre-wrap text-ele-white">{description.trim()}</dd>
            </div>
            <div>
              <dt className="text-ele-body">Is the lift out of service?</dt>
              <dd className="text-ele-white">{serviceLabel}</dd>
            </div>
          </dl>
          {formError && (
            <p role="alert" className="mt-3 text-sm text-ele-danger">
              {formError}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={send}
              disabled={sending}
              className="min-h-11 rounded-ele bg-ele-green px-5 py-2 font-heading text-sm tracking-wide text-ele-bg disabled:opacity-60"
            >
              {sending ? "Sending..." : "Send report"}
            </button>
            <button
              type="button"
              onClick={() => setStep("form")}
              disabled={sending}
              className="min-h-11 rounded-ele border border-white/20 px-5 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-white"
            >
              Go back and change it
            </button>
          </div>
        </div>
      ) : (
        <form
          className={`${box} space-y-4`}
          onSubmit={(e) => {
            e.preventDefault();
            check();
          }}
          noValidate
        >
          <div>
            <label htmlFor="fault-lift" className="mb-1 block text-sm text-ele-white">
              Which lift?
            </label>
            <select
              id="fault-lift"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              className="min-h-11 w-full rounded-ele border border-white/20 bg-white px-3 py-2 text-sm text-ele-white"
            >
              <option value="">Choose a lift</option>
              {choices.map((c) => (
                <option key={c.assetId} value={c.assetId}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="fault-description" className="mb-1 block text-sm text-ele-white">
              What is wrong?
            </label>
            <textarea
              id="fault-description"
              value={description}
              maxLength={FAULT_DESCRIPTION_MAX}
              rows={5}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="For example: the doors keep opening and closing on the second floor."
              className="w-full rounded-ele border border-white/20 bg-white px-3 py-2 text-sm text-ele-white"
            />
          </div>
          <fieldset>
            <legend className="mb-1 text-sm text-ele-white">Is the lift out of service?</legend>
            <div className="flex flex-wrap gap-4 text-sm text-ele-body">
              {(
                [
                  ["yes", "Yes"],
                  ["no", "No, it is still working"],
                  ["unsure", "Not sure"],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex min-h-11 items-center gap-2">
                  <input type="radio" name="fault-service" value={value} checked={service === value} onChange={() => setService(value)} />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          {formError && (
            <p role="alert" className="text-sm text-ele-danger">
              {formError}
            </p>
          )}
          <p className="text-xs text-ele-body">Our office will be in touch about your report.</p>
          <button type="submit" className="min-h-11 rounded-ele bg-ele-green px-5 py-2 font-heading text-sm tracking-wide text-ele-bg">
            Check and send
          </button>
        </form>
      )}
    </PortalShell>
  );
}
